import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Key,
  Building2,
  Shield,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Plus,
  Settings,
  Link,
  Trash2,
  RefreshCw,
  Loader2,
  FileText,
  Users,
  Globe,
  Lock,
  Unlock,
  Copy,
  ExternalLink,
  Zap,
  Database,
  Server
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available Partner Services
const PARTNER_SERVICES = [
  {
    id: 'northeast_ra',
    name: 'Northeast Registered Agent',
    category: 'Registered Agent',
    description: 'Business formation and registered agent services',
    icon: Building2,
    status: 'active',
    requiredKeys: ['api_key', 'secret_key'],
    endpoints: [
      { name: 'Check Entity Name', method: 'POST', path: '/check-name' },
      { name: 'Create Entity', method: 'POST', path: '/create-entity' },
      { name: 'Get Entity Status', method: 'GET', path: '/entity-status' },
      { name: 'File Documents', method: 'POST', path: '/file-documents' }
    ]
  },
  {
    id: 'state_filing',
    name: 'State Filing Service',
    category: 'Filing',
    description: 'Direct state filing and compliance',
    icon: FileText,
    status: 'coming_soon',
    requiredKeys: ['api_key'],
    endpoints: []
  },
  {
    id: 'ein_service',
    name: 'EIN Application Service',
    category: 'Tax',
    description: 'Federal EIN acquisition service',
    icon: Database,
    status: 'coming_soon',
    requiredKeys: ['api_key', 'account_id'],
    endpoints: []
  },
  {
    id: 'compliance_monitor',
    name: 'Compliance Monitor',
    category: 'Compliance',
    description: 'Ongoing compliance monitoring',
    icon: Shield,
    status: 'coming_soon',
    requiredKeys: ['api_key'],
    endpoints: []
  }
];

const ServicesPartners = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('services');
  const [connectedServices, setConnectedServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  
  // API Key inputs
  const [apiKeys, setApiKeys] = useState({});
  
  // Northeast RA specific state
  const [entityCheckName, setEntityCheckName] = useState('');
  const [entityState, setEntityState] = useState('');
  const [entityType, setEntityType] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [isChecking, setIsChecking] = useState(false);

  // Load connected services
  useEffect(() => {
    loadConnectedServices();
  }, []);

  const loadConnectedServices = async () => {
    try {
      const response = await axios.get(`${API}/partners/connected`);
      setConnectedServices(response.data.services || []);
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const connectService = async (serviceId) => {
    const service = PARTNER_SERVICES.find(s => s.id === serviceId);
    if (!service) return;

    // Validate all required keys are provided
    const missingKeys = service.requiredKeys.filter(key => !apiKeys[key]);
    if (missingKeys.length > 0) {
      toast.error(`Please provide all required keys: ${missingKeys.join(', ')}`);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/partners/connect`, {
        service_id: serviceId,
        credentials: apiKeys
      });

      if (response.data.success) {
        toast.success(`Connected to ${service.name}`);
        setShowKeyDialog(false);
        setApiKeys({});
        loadConnectedServices();
      }
    } catch (error) {
      toast.error('Failed to connect service');
      console.error('Connect error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const disconnectService = async (serviceId) => {
    if (!confirm('Are you sure you want to disconnect this service?')) return;

    try {
      await axios.delete(`${API}/partners/disconnect/${serviceId}`);
      toast.success('Service disconnected');
      loadConnectedServices();
    } catch (error) {
      toast.error('Failed to disconnect service');
    }
  };

  const testConnection = async (serviceId) => {
    setIsTesting(true);
    try {
      const response = await axios.post(`${API}/partners/test/${serviceId}`);
      if (response.data.success) {
        toast.success('Connection test successful!');
      } else {
        toast.error('Connection test failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setIsTesting(false);
    }
  };

  const toggleShowSecret = (key) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Northeast RA - Check Entity Name
  const checkEntityName = async () => {
    if (!entityCheckName || !entityState || !entityType) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsChecking(true);
    setCheckResult(null);
    try {
      const response = await axios.post(`${API}/partners/northeast-ra/check-name`, {
        entity_name: entityCheckName,
        state: entityState,
        entity_type: entityType
      });
      setCheckResult(response.data);
    } catch (error) {
      toast.error('Error checking entity name');
      console.error('Check name error:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const isServiceConnected = (serviceId) => {
    return connectedServices.some(s => s.service_id === serviceId);
  };

  const getConnectedService = (serviceId) => {
    return connectedServices.find(s => s.service_id === serviceId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-900 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-blue-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Server className="w-8 h-8 text-amber-400" />
              <div>
                <h1 className="text-xl font-bold">Services & Partners</h1>
                <p className="text-blue-200 text-sm">Companeze Corp Builder</p>
              </div>
            </div>
          </div>
          <Badge className="bg-amber-500 text-white">
            {connectedServices.length} Connected
          </Badge>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border shadow-sm p-1">
            <TabsTrigger value="services" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <Link className="w-4 h-4 mr-2" />
              Partner Services
            </TabsTrigger>
            <TabsTrigger value="secrets" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <Key className="w-4 h-4 mr-2" />
              API Secrets
            </TabsTrigger>
            <TabsTrigger value="northeast" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <Building2 className="w-4 h-4 mr-2" />
              Northeast RA
            </TabsTrigger>
          </TabsList>

          {/* Partner Services Tab */}
          <TabsContent value="services">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {PARTNER_SERVICES.map((service) => {
                const Icon = service.icon;
                const connected = isServiceConnected(service.id);
                const connectedData = getConnectedService(service.id);

                return (
                  <Card key={service.id} className={`relative ${connected ? 'border-green-500 border-2' : ''}`}>
                    {connected && (
                      <div className="absolute top-3 right-3">
                        <Badge className="bg-green-500 text-white">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Connected
                        </Badge>
                      </div>
                    )}
                    
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          service.status === 'active' ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          <Icon className={`w-6 h-6 ${
                            service.status === 'active' ? 'text-blue-900' : 'text-gray-400'
                          }`} />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{service.name}</CardTitle>
                          <CardDescription>{service.category}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-4">{service.description}</p>
                      
                      {service.status === 'coming_soon' ? (
                        <Button disabled className="w-full" variant="outline">
                          Coming Soon
                        </Button>
                      ) : connected ? (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              className="flex-1"
                              onClick={() => testConnection(service.id)}
                              disabled={isTesting}
                            >
                              {isTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                              Test
                            </Button>
                            <Button 
                              variant="outline" 
                              className="flex-1 text-red-600 hover:bg-red-50"
                              onClick={() => disconnectService(service.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Disconnect
                            </Button>
                          </div>
                          <p className="text-xs text-gray-500 text-center">
                            Connected: {connectedData?.connected_at ? new Date(connectedData.connected_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      ) : (
                        <Button 
                          className="w-full bg-blue-900 hover:bg-blue-800"
                          onClick={() => {
                            setSelectedService(service);
                            setShowKeyDialog(true);
                          }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Connect Service
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* API Secrets Tab */}
          <TabsContent value="secrets">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-amber-500" />
                  API Keys & Secrets
                </CardTitle>
                <CardDescription>
                  Manage your connected service credentials securely
                </CardDescription>
              </CardHeader>
              <CardContent>
                {connectedServices.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Lock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No services connected yet</p>
                    <p className="text-sm mt-2">Connect a partner service to manage API keys</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {connectedServices.map((service) => {
                      const serviceInfo = PARTNER_SERVICES.find(s => s.id === service.service_id);
                      
                      return (
                        <div key={service.service_id} className="p-4 bg-gray-50 rounded-lg border">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-5 h-5 text-blue-900" />
                              <span className="font-semibold">{serviceInfo?.name || service.service_id}</span>
                              <Badge className="bg-green-100 text-green-700">Active</Badge>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => testConnection(service.service_id)}>
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Test
                            </Button>
                          </div>
                          
                          <div className="space-y-2">
                            {Object.entries(service.credentials || {}).map(([key, value]) => (
                              <div key={key} className="flex items-center gap-2 bg-white p-2 rounded border">
                                <span className="text-sm font-medium text-gray-600 w-24">{key}:</span>
                                <code className="flex-1 text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                                  {showSecrets[`${service.service_id}_${key}`] 
                                    ? value 
                                    : '••••••••••••••••••••••••'
                                  }
                                </code>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => toggleShowSecret(`${service.service_id}_${key}`)}
                                >
                                  {showSecrets[`${service.service_id}_${key}`] 
                                    ? <EyeOff className="w-4 h-4" />
                                    : <Eye className="w-4 h-4" />
                                  }
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => copyToClipboard(value)}
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Northeast Registered Agent Tab */}
          <TabsContent value="northeast">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Entity Name Check */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-900" />
                    Entity Name Availability
                  </CardTitle>
                  <CardDescription>
                    Check if your business name is available in your state
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Entity Name</Label>
                    <Input 
                      placeholder="e.g., My Business LLC"
                      value={entityCheckName}
                      onChange={(e) => setEntityCheckName(e.target.value)}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Select value={entityState} onValueChange={setEntityState}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CA">California</SelectItem>
                          <SelectItem value="DE">Delaware</SelectItem>
                          <SelectItem value="FL">Florida</SelectItem>
                          <SelectItem value="NY">New York</SelectItem>
                          <SelectItem value="TX">Texas</SelectItem>
                          <SelectItem value="WY">Wyoming</SelectItem>
                          <SelectItem value="NV">Nevada</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Entity Type</Label>
                      <Select value={entityType} onValueChange={setEntityType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LLC">LLC</SelectItem>
                          <SelectItem value="CORP">Corporation</SelectItem>
                          <SelectItem value="SCORP">S-Corporation</SelectItem>
                          <SelectItem value="LP">Limited Partnership</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full bg-blue-900 hover:bg-blue-800"
                    onClick={checkEntityName}
                    disabled={isChecking || !isServiceConnected('northeast_ra')}
                  >
                    {isChecking ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking...</>
                    ) : (
                      <>Check Availability</>
                    )}
                  </Button>
                  
                  {!isServiceConnected('northeast_ra') && (
                    <p className="text-sm text-amber-600 text-center">
                      ⚠️ Connect Northeast RA service first in Partner Services tab
                    </p>
                  )}
                  
                  {/* Check Result */}
                  {checkResult && (
                    <div className={`p-4 rounded-lg ${
                      checkResult.available 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        {checkResult.available ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className={`font-semibold ${
                          checkResult.available ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {checkResult.available ? 'Name Available!' : 'Name Not Available'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{checkResult.message}</p>
                      {checkResult.suggestions && checkResult.suggestions.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-semibold text-gray-500 mb-1">Suggestions:</p>
                          <ul className="text-sm text-gray-600 space-y-1">
                            {checkResult.suggestions.map((s, i) => (
                              <li key={i}>• {s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Service Status & Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-500" />
                    Northeast RA Actions
                  </CardTitle>
                  <CardDescription>
                    Available API endpoints and actions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {PARTNER_SERVICES.find(s => s.id === 'northeast_ra')?.endpoints.map((endpoint, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className={
                            endpoint.method === 'POST' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'
                          }>
                            {endpoint.method}
                          </Badge>
                          <span className="font-medium">{endpoint.name}</span>
                        </div>
                        <code className="text-xs text-gray-500">{endpoint.path}</code>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-2">Connection Status</h4>
                    {isServiceConnected('northeast_ra') ? (
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle2 className="w-5 h-5" />
                        <span>Service Connected & Active</span>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-amber-700">
                          <AlertCircle className="w-5 h-5" />
                          <span>Service Not Connected</span>
                        </div>
                        <Button 
                          className="w-full bg-blue-900 hover:bg-blue-800"
                          onClick={() => {
                            setSelectedService(PARTNER_SERVICES.find(s => s.id === 'northeast_ra'));
                            setShowKeyDialog(true);
                          }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Connect Northeast RA
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Connect Service Dialog */}
      <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Connect {selectedService?.name}
            </DialogTitle>
            <DialogDescription>
              Enter your API credentials to connect this service
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedService?.requiredKeys.map((keyName) => (
              <div key={keyName} className="space-y-2">
                <Label className="capitalize">{keyName.replace('_', ' ')}</Label>
                <div className="relative">
                  <Input 
                    type={showSecrets[keyName] ? 'text' : 'password'}
                    placeholder={`Enter ${keyName}`}
                    value={apiKeys[keyName] || ''}
                    onChange={(e) => setApiKeys(prev => ({ ...prev, [keyName]: e.target.value }))}
                    className="pr-10"
                  />
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2"
                    onClick={() => toggleShowSecret(keyName)}
                  >
                    {showSecrets[keyName] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            ))}
            
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Your credentials are encrypted and stored securely.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowKeyDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-blue-900 hover:bg-blue-800"
              onClick={() => connectService(selectedService?.id)}
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesPartners;
